import { AlertTriangle, Check, Flag, Lock, RefreshCw, UserX } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  DataTable,
  type DataTableColumn,
} from "@/components/cinch/DataTable/DataTable";
import {
  ActiveFilterPill,
  CheckboxList,
  FilterBar,
  FilterChip,
  FilterClearAll,
  FilterSearch,
} from "@/components/cinch/FilterBar";
import { BulkActionBar } from "@/components/cinch/BulkActionBar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { IconButton } from "@/components/cinch/IconButton";
import { KpiTile } from "@/components/cinch/KpiTile";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  SIGNAL_META,
  useBulkReviewInternalItems,
  useDismissInternalCandidate,
  useInternalAccounts,
  usePullInternalAccounts,
  useReviewInternalItem,
  type InternalAccountRow,
  type InternalCandidate,
  type InternalCompany,
} from "../api";

const num = (n: number | null | undefined) =>
  n == null ? "—" : n.toLocaleString("en-US");

const money = (n: number | null | undefined) =>
  n == null
    ? "—"
    : n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const day = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString("en-US") : "—";

/** Who verified or flagged the row, and when.
 *
 *  Its own column rather than a hover, because this step feeds accounting: an
 *  internal charge that lands on a department's budget has to name the person
 *  who accepted it, on the same line as the number they accepted. */
function Reviewer({
  name,
  at,
}: {
  name: string | null;
  at: string | null;
}) {
  if (!name) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="flex flex-col leading-tight">
      <span className="text-xs font-medium">{name}</span>
      {at && (
        <span className="text-[11px] text-muted-foreground">
          {new Date(at).toLocaleDateString("en-US")}
        </span>
      )}
    </span>
  );
}

/** The review chip, with the tone every other reconciliation panel uses.
 *
 *  `value` stays the RAW status so StatusBadge's map owns the color — it knows
 *  approved/flagged/pending and nothing else. Only the word differs per tab (a
 *  roster row is "verified", a candidate is "confirmed" as internal), so that
 *  goes through `label`. Passing "verified" as the value misses the map and
 *  silently renders info-blue instead of success-green. */
function ReviewChip({
  status,
  approvedLabel,
}: {
  status: string;
  approvedLabel: string;
}) {
  return (
    <StatusBadge
      value={status}
      label={status === "approved" ? approvedLabel : undefined}
    />
  );
}

function ReviewButtons({
  status,
  disabled,
  onReview,
}: {
  status: string;
  disabled: boolean;
  onReview: (status: "approved" | "flagged") => void;
}) {
  return (
    <span className="flex items-center gap-1">
      <IconButton
        title="Verify — this company belongs on this account"
        active={status === "approved"}
        activeClassName="text-success"
        hoverClassName="hover:bg-success-light hover:text-success"
        disabled={disabled}
        onClick={() => onReview("approved")}
      >
        <Check className="h-3.5 w-3.5" />
      </IconButton>
      <IconButton
        title="Flag — blocks completing R-17 until it's resolved"
        active={status === "flagged"}
        activeClassName="text-warning"
        hoverClassName="hover:bg-warning-light hover:text-warning"
        disabled={disabled}
        onClick={() => onReview("flagged")}
      >
        <Flag className="h-3.5 w-3.5" />
      </IconButton>
    </span>
  );
}

export function InternalAccountsPanel({ period }: { period: string }) {
  const { data, isLoading } = useInternalAccounts(period);
  const pull = usePullInternalAccounts(period);
  const review = useReviewInternalItem(period);
  const bulkReview = useBulkReviewInternalItems(period);
  const dismiss = useDismissInternalCandidate(period);

  const [tab, setTab] = useState("companies");
  // Multi-select throughout, per the FilterBar idiom: "Product AND R&D" is a
  // question worth asking and a single-select picker couldn't express it.
  const [accountFilter, setAccountFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [usageFilter, setUsageFilter] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [candidateSearch, setCandidateSearch] = useState("");
  const [signalFilter, setSignalFilter] = useState<string[]>([]);

  const canAct = data?.can_act ?? false;
  const locked = data?.locked ?? false;
  // Only meaningful once something has been pulled — an un-pulled step has no
  // scan to be partial.
  const partialScan = !!data?.pulled_at && data?.users_signal_ok === false;
  const summary = data?.summary;
  // Memoised, not `data?.accounts ?? []` inline: a fresh [] every render would
  // re-run the filter memos below on every keystroke.
  const accounts = useMemo(() => data?.accounts ?? [], [data?.accounts]);

  const companies = useMemo(() => {
    let rows = data?.companies ?? [];
    if (accountFilter.length)
      rows = rows.filter((r) => r.account_id && accountFilter.includes(r.account_id));
    if (statusFilter.length)
      rows = rows.filter((r) => statusFilter.includes(r.review_status));
    if (usageFilter.length) {
      rows = rows.filter((r) => {
        const sent = r.text_segments > 0;
        return (
          (usageFilter.includes("sent") && sent) ||
          (usageFilter.includes("idle") && !sent)
        );
      });
    }
    const q = search.trim().toLowerCase();
    if (q)
      rows = rows.filter((r) =>
        [r.company_name, r.account_name, r.department].some((v) =>
          (v ?? "").toLowerCase().includes(q),
        ),
      );
    return rows;
  }, [data?.companies, accountFilter, statusFilter, usageFilter, search]);

  const candidates = useMemo(() => {
    let rows = data?.candidates ?? [];
    if (signalFilter.length)
      rows = rows.filter((r) => r.signals.some((s) => signalFilter.includes(s)));
    const q = candidateSearch.trim().toLowerCase();
    if (q)
      rows = rows.filter((r) =>
        [r.company_name, r.account_name].some((v) =>
          (v ?? "").toLowerCase().includes(q),
        ),
      );
    return rows;
  }, [data?.candidates, signalFilter, candidateSearch]);

  const accountCounts = useMemo(
    () =>
      accounts
        .filter((a) => a.company_count > 0)
        .map((a) => ({
          value: a.cinch_account_id,
          label: a.name,
          count: a.company_count,
        })),
    [accounts],
  );

  const statusCounts = useMemo(() => {
    const rows = data?.companies ?? [];
    return (["pending", "approved", "flagged"] as const)
      .map((s) => ({
        value: s,
        label: s === "approved" ? "verified" : s,
        count: rows.filter((r) => r.review_status === s).length,
      }))
      .filter((o) => o.count > 0);
  }, [data?.companies]);

  const activeFilters = useMemo(
    () => [
      ...accountFilter.map((v) => ({
        kind: "account" as const,
        value: v,
        label: accounts.find((a) => a.cinch_account_id === v)?.name ?? v,
      })),
      ...statusFilter.map((v) => ({
        kind: "status" as const,
        value: v,
        label: v === "approved" ? "verified" : v,
      })),
      ...usageFilter.map((v) => ({
        kind: "usage" as const,
        value: v,
        label: v === "sent" ? "sent texts" : "no texts",
      })),
    ],
    [accountFilter, statusFilter, usageFilter, accounts],
  );

  function clearFilter(kind: string, value: string) {
    if (kind === "account") setAccountFilter(accountFilter.filter((v) => v !== value));
    if (kind === "status") setStatusFilter(statusFilter.filter((v) => v !== value));
    if (kind === "usage") setUsageFilter(usageFilter.filter((v) => v !== value));
  }

  async function runPull() {
    try {
      await pull.mutateAsync();
      toast.success("Internal accounts pulled");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't pull the worklist");
    }
  }

  async function quickReview(itemKey: string, status: "approved" | "flagged") {
    try {
      await review.mutateAsync({ item_key: itemKey, review_status: status });
      toast.success(status === "approved" ? "Verified" : "Flagged");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save that review");
    }
  }

  /** Applies to exactly the rows the table is showing — `rows` is already the
   *  filtered+searched set, so scope and screen agree by construction. */
  async function bulkReviewVisible(
    rows: { item_key: string }[],
    status: "approved" | "flagged",
    noun: string,
    plural: string,
  ) {
    try {
      const res = await bulkReview.mutateAsync({
        item_keys: rows.map((r) => r.item_key),
        review_status: status,
      });
      const verb = status === "approved" ? "Verified" : "Flagged";
      toast.success(
        res.updated === res.requested
          ? `${verb} ${res.updated} ${res.updated === 1 ? noun : plural}`
          : `${verb} ${res.updated} of ${res.requested} — the rest are no longer in this worklist`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Bulk review failed");
    }
  }

  async function dismissCandidate(row: InternalCandidate) {
    try {
      await dismiss.mutateAsync({
        cinch_company_id: row.company_id ?? "",
        company_name: row.company_name,
        account_name: row.account_name,
      });
      toast.success(`${row.company_name ?? "Company"} won't be suggested again`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't dismiss that");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">
          {data?.pulled_at ? (
            <>
              Pulled {new Date(data.pulled_at).toLocaleString()}
              {data.pulled_by_name && ` by ${data.pulled_by_name}`}
            </>
          ) : (
            "Not pulled yet for this close."
          )}
        </div>
        <div className="flex items-center gap-2">
          {locked && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Lock className="h-3.5 w-3.5" /> step complete
            </span>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={!canAct || locked || pull.isPending}
            onClick={runPull}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${pull.isPending ? "animate-spin" : ""}`}
            />
            {data?.pulled_at ? "Re-pull" : "Pull the month"}
          </Button>
        </div>
      </div>

      {partialScan && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Recommended list is incomplete</AlertTitle>
          <AlertDescription>
            This pull could not read the Cinch-users source in Snowflake, so
            candidates were matched on company name alone — a company whose
            only tell is that every user on it is @cinch.io will be missing.
            The Companies tab is unaffected. R-17 stays open until a pull
            completes with both signals.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <KpiTile label="Companies" value={num(summary?.company_count)} />
        <KpiTile
          label="Contacts"
          value={num(summary?.contacts)}
          info="Contacts held across every company on an internal account, for the close month. The largest number on this page by far, and the one that would cost real money the day contacts are charged back."
        />
        <KpiTile
          label="Text segments"
          value={num(summary?.text_segments)}
          sub={`${num(summary?.text_messages)} messages`}
          info="Segments are the billable unit — a long message costs several. The message count is shown beneath because the two diverge a lot."
        />
        <KpiTile
          label="Awaiting review"
          value={num(summary?.pending_count)}
          tone={summary?.pending_count ? "warning" : "positive"}
        />
        <KpiTile
          label="Flagged"
          value={num(summary?.flagged_count)}
          tone={summary?.flagged_count ? "danger" : "neutral"}
          info="An open flag blocks completing R-17 — completing the step is the attestation that every company here belongs here."
        />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="companies">
            Companies{summary?.company_count ? ` (${summary.company_count})` : ""}
          </TabsTrigger>
          <TabsTrigger value="candidates">
            Recommended{summary?.candidate_count ? ` (${summary.candidate_count})` : ""}
          </TabsTrigger>
          <TabsTrigger value="accounts">Accounts ({accounts.length})</TabsTrigger>
          <TabsTrigger value="howto">How-to</TabsTrigger>
        </TabsList>

        <TabsContent value="companies" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                Companies on internal accounts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FilterBar>
                <FilterSearch
                  value={search}
                  onChange={setSearch}
                  placeholder="Search company, account, department…"
                />
                <FilterChip label="Account" count={accountFilter.length}>
                  <CheckboxList
                    searchable={accountCounts.length > 6}
                    options={accountCounts}
                    selected={accountFilter}
                    onChange={setAccountFilter}
                    emptyText="Pull the month first"
                  />
                </FilterChip>
                <FilterChip label="Review" count={statusFilter.length}>
                  <CheckboxList
                    options={statusCounts}
                    selected={statusFilter}
                    onChange={setStatusFilter}
                    emptyText="Pull the month first"
                  />
                </FilterChip>
                <FilterChip label="Usage" count={usageFilter.length}>
                  <CheckboxList
                    options={[
                      { value: "sent", label: "sent texts" },
                      { value: "idle", label: "no texts" },
                    ]}
                    selected={usageFilter}
                    onChange={setUsageFilter}
                  />
                </FilterChip>
                {activeFilters.map((f) => (
                  <ActiveFilterPill
                    key={`${f.kind}:${f.value}`}
                    label={f.label}
                    onRemove={() => clearFilter(f.kind, f.value)}
                  />
                ))}
                {activeFilters.length > 0 && (
                  <FilterClearAll
                    onClick={() => {
                      setAccountFilter([]);
                      setStatusFilter([]);
                      setUsageFilter([]);
                    }}
                  />
                )}
              </FilterBar>

              {canAct && !locked && (
                <BulkActionBar
                  className="mt-3"
                  count={companies.length}
                  totalCount={data?.companies.length ?? 0}
                  noun="company"
                  nounPlural="companies"
                  actions={[
                    {
                      label: "Verify",
                      tone: "success",
                      icon: <Check className="h-3.5 w-3.5" />,
                      disabled: bulkReview.isPending,
                      onClick: () =>
                        bulkReviewVisible(companies, "approved", "company", "companies"),
                    },
                    {
                      label: "Flag",
                      tone: "warning",
                      icon: <Flag className="h-3.5 w-3.5" />,
                      disabled: bulkReview.isPending,
                      onClick: () =>
                        bulkReviewVisible(companies, "flagged", "company", "companies"),
                    },
                  ]}
                />
              )}

              <div className="mt-4">
                <DataTable
                  rows={companies}
                  rowKey={(r) => r.item_key}
                  loading={isLoading}
                  emptyMessage={
                    data?.pulled_at
                      ? "No companies match those filters."
                      : "Pull the month to build the worklist."
                  }
                  columns={companyColumns(canAct && !locked, quickReview)}
                  footer={(cols) => (
                    <tr className="border-t bg-muted/40 font-medium">
                      <td className="px-3 py-2 text-sm" colSpan={3}>
                        {companies.length.toLocaleString()} compan
                        {companies.length === 1 ? "y" : "ies"}
                      </td>
                      <td className="px-3 py-2 text-right text-sm tabular-nums">
                        {num(companies.reduce((s, r) => s + r.contacts, 0))}
                      </td>
                      <td className="px-3 py-2 text-right text-sm tabular-nums">
                        {num(companies.reduce((s, r) => s + r.text_messages, 0))}
                      </td>
                      <td className="px-3 py-2 text-right text-sm tabular-nums">
                        {num(companies.reduce((s, r) => s + r.text_segments, 0))}
                      </td>
                      <td
                        className="px-3 py-2 text-right text-sm text-muted-foreground"
                        colSpan={cols.length - 6}
                      >
                        —
                      </td>
                    </tr>
                  )}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="candidates" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                Might belong on an internal account
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Companies on <em>customer</em> accounts that look like our own
                usage. Anything that actually sent texts this month sorts first —
                that is a live cost question. The rest is structural cleanup, and
                dismissing one keeps it off every future close.
              </p>
            </CardHeader>
            <CardContent>
              <FilterBar>
                <FilterSearch
                  value={candidateSearch}
                  onChange={setCandidateSearch}
                  placeholder="Search company or account…"
                />
                <FilterChip label="Signal" count={signalFilter.length}>
                  <CheckboxList
                    options={Object.entries(SIGNAL_META).map(([value, m]) => ({
                      value,
                      label: m.label,
                      count: (data?.candidates ?? []).filter((c) =>
                        c.signals.includes(value),
                      ).length,
                    }))}
                    selected={signalFilter}
                    onChange={setSignalFilter}
                  />
                </FilterChip>
                {signalFilter.map((v) => (
                  <ActiveFilterPill
                    key={v}
                    label={SIGNAL_META[v]?.label ?? v}
                    onRemove={() =>
                      setSignalFilter(signalFilter.filter((s) => s !== v))
                    }
                  />
                ))}
                {signalFilter.length > 0 && (
                  <FilterClearAll onClick={() => setSignalFilter([])} />
                )}
              </FilterBar>

              {canAct && !locked && (
                <BulkActionBar
                  className="mt-3"
                  count={candidates.length}
                  totalCount={data?.candidates.length ?? 0}
                  noun="candidate"
                  actions={[
                    {
                      label: "Confirm internal",
                      tone: "success",
                      icon: <Check className="h-3.5 w-3.5" />,
                      disabled: bulkReview.isPending,
                      onClick: () =>
                        bulkReviewVisible(candidates, "approved", "candidate", "candidates"),
                    },
                    {
                      label: "Flag",
                      tone: "warning",
                      icon: <Flag className="h-3.5 w-3.5" />,
                      disabled: bulkReview.isPending,
                      onClick: () =>
                        bulkReviewVisible(candidates, "flagged", "candidate", "candidates"),
                    },
                  ]}
                />
              )}

              <div className="mt-4">
                <DataTable
                  rows={candidates}
                  rowKey={(r) => r.item_key}
                  loading={isLoading}
                  emptyMessage={
                    data?.pulled_at
                      ? "Nothing outside the roster looks internal this month."
                      : "Pull the month to build the worklist."
                  }
                  columns={candidateColumns(
                    canAct && !locked,
                    quickReview,
                    dismissCandidate,
                    dismiss.isPending,
                  )}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="accounts" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">The internal roster</CardTitle>
              <p className="text-sm text-muted-foreground">
                A stored list, deliberately — <code>ACCOUNT_TYPE = 'Internal'</code>{" "}
                in the mart tags only six of these ten, so deriving the set would
                silently drop four accounts and keep dropping every new one
                nobody remembers to tag.
              </p>
            </CardHeader>
            <CardContent>
              <DataTable
                rows={accounts}
                rowKey={(r) => r.cinch_account_id}
                loading={isLoading}
                emptyMessage="No internal accounts configured."
                columns={accountColumns()}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="howto" className="mt-4">
          <HowToTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function companyColumns(
  canAct: boolean,
  quickReview: (itemKey: string, status: "approved" | "flagged") => void,
): DataTableColumn<InternalCompany>[] {
  return [
    {
      key: "account_name",
      header: "Account",
      sortable: true,
      render: (r) => (
        <span className="flex flex-col leading-tight">
          <span>{r.account_name ?? "—"}</span>
          {r.department && (
            <span className="text-xs text-muted-foreground">{r.department}</span>
          )}
        </span>
      ),
    },
    {
      key: "company_name",
      header: "Company",
      sortable: true,
      render: (r) => (
        <span className="flex flex-col leading-tight">
          <span className="font-medium">{r.company_name ?? "—"}</span>
          {!r.company_active && (
            <span className="text-xs text-muted-foreground">inactive</span>
          )}
        </span>
      ),
    },
    {
      key: "company_created",
      header: "Created",
      sortable: true,
      align: "right",
      render: (r) => <span className="tabular-nums">{day(r.company_created)}</span>,
    },
    {
      key: "contacts",
      header: "Contacts",
      sortable: true,
      align: "right",
      numeric: true,
      render: (r) => <span className="tabular-nums">{num(r.contacts)}</span>,
    },
    {
      key: "text_messages",
      header: "Messages",
      sortable: true,
      align: "right",
      numeric: true,
      render: (r) => <span className="tabular-nums">{num(r.text_messages)}</span>,
    },
    {
      key: "text_segments",
      header: "Segments",
      sortable: true,
      align: "right",
      numeric: true,
      render: (r) => <span className="tabular-nums">{num(r.text_segments)}</span>,
    },
    {
      // Deliberately blank until automated billing can price internal usage.
      // Shipped as a real column so the shape of the report doesn't change
      // under anyone the month it starts carrying numbers.
      key: "amount_billed",
      header: "Billed",
      align: "right",
      numeric: true,
      render: (r) =>
        r.amount_billed == null ? (
          <span className="text-muted-foreground" title="Not billed back yet — this column fills in once internal usage is priced automatically.">
            —
          </span>
        ) : (
          <span className="tabular-nums">{money(r.amount_billed)}</span>
        ),
    },
    {
      key: "review_status",
      header: "Review",
      render: (r) => (
        <ReviewChip status={r.review_status} approvedLabel="Verified" />
      ),
    },
    {
      key: "reviewed_by_name",
      header: "Reviewed by",
      render: (r) => <Reviewer name={r.reviewed_by_name} at={r.reviewed_at} />,
    },
    ...(canAct
      ? [
          {
            key: "actions",
            header: "",
            align: "right" as const,
            render: (r: InternalCompany) => (
              <ReviewButtons
                status={r.review_status}
                disabled={false}
                onReview={(s) => quickReview(r.item_key, s)}
              />
            ),
          },
        ]
      : []),
  ];
}

function candidateColumns(
  canAct: boolean,
  quickReview: (itemKey: string, status: "approved" | "flagged") => void,
  onDismiss: (row: InternalCandidate) => void,
  dismissing: boolean,
): DataTableColumn<InternalCandidate>[] {
  return [
    {
      key: "account_name",
      header: "Account",
      sortable: true,
      render: (r) => r.account_name ?? "—",
    },
    {
      key: "company_name",
      header: "Company",
      sortable: true,
      render: (r) => (
        <span className="flex flex-col leading-tight">
          <span className="font-medium">{r.company_name ?? "—"}</span>
          {r.has_usage && (
            <span className="text-xs text-warning">sent texts this month</span>
          )}
        </span>
      ),
    },
    {
      key: "signals",
      header: "Why",
      render: (r) => (
        <span className="flex flex-wrap gap-1">
          {r.signals.map((s) => (
            <span key={s} title={SIGNAL_META[s]?.hint}>
              <StatusBadge value={SIGNAL_META[s]?.label ?? s} />
            </span>
          ))}
          {r.signals.includes("cinch_users") && (
            <span className="text-xs text-muted-foreground">
              {r.users_cinch}/{r.users_all}
            </span>
          )}
        </span>
      ),
    },
    {
      key: "company_created",
      header: "Created",
      sortable: true,
      align: "right",
      render: (r) => <span className="tabular-nums">{day(r.company_created)}</span>,
    },
    {
      key: "contacts",
      header: "Contacts",
      sortable: true,
      align: "right",
      numeric: true,
      render: (r) => <span className="tabular-nums">{num(r.contacts)}</span>,
    },
    {
      key: "text_segments",
      header: "Segments",
      sortable: true,
      align: "right",
      numeric: true,
      render: (r) => (
        <span className={r.text_segments > 0 ? "font-medium tabular-nums" : "tabular-nums"}>
          {num(r.text_segments)}
        </span>
      ),
    },
    {
      key: "review_status",
      header: "Review",
      render: (r) => (
        <ReviewChip status={r.review_status} approvedLabel="Confirmed" />
      ),
    },
    {
      key: "reviewed_by_name",
      header: "Reviewed by",
      render: (r) => <Reviewer name={r.reviewed_by_name} at={r.reviewed_at} />,
    },
    ...(canAct
      ? [
          {
            key: "actions",
            header: "",
            align: "right" as const,
            render: (r: InternalCandidate) => (
              <span className="flex items-center gap-1">
                <ReviewButtons
                  status={r.review_status}
                  disabled={false}
                  onReview={(s) => quickReview(r.item_key, s)}
                />
                <IconButton
                  title="Not internal — never suggest this company again"
                  hoverClassName="hover:bg-muted hover:text-foreground"
                  disabled={dismissing}
                  onClick={() => onDismiss(r)}
                >
                  <UserX className="h-3.5 w-3.5" />
                </IconButton>
              </span>
            ),
          },
        ]
      : []),
  ];
}

function accountColumns(): DataTableColumn<InternalAccountRow>[] {
  return [
    {
      key: "name",
      header: "Account",
      sortable: true,
      render: (r) => (
        <span className="flex flex-col leading-tight">
          <span className="font-medium">{r.name}</span>
          {!r.is_active && (
            <span className="text-xs text-muted-foreground">off the roster</span>
          )}
        </span>
      ),
    },
    {
      key: "department",
      header: "Charged to",
      render: (r) =>
        r.department ? (
          r.department
        ) : (
          <span
            className="text-warning"
            title="No budget owner set — this account's usage has nowhere to land."
          >
            needs a department
          </span>
        ),
    },
    {
      key: "quickbooks_id",
      header: "QB id",
      align: "right",
      render: (r) => (
        <span className="font-mono text-xs">{r.quickbooks_id ?? "—"}</span>
      ),
    },
    {
      key: "company_count",
      header: "Companies",
      sortable: true,
      align: "right",
      numeric: true,
      render: (r) => <span className="tabular-nums">{num(r.company_count)}</span>,
    },
    {
      key: "contacts",
      header: "Contacts",
      sortable: true,
      align: "right",
      numeric: true,
      render: (r) => <span className="tabular-nums">{num(r.contacts)}</span>,
    },
    {
      key: "text_segments",
      header: "Segments",
      sortable: true,
      align: "right",
      numeric: true,
      render: (r) => <span className="tabular-nums">{num(r.text_segments)}</span>,
    },
    {
      key: "pending_count",
      header: "Awaiting",
      align: "right",
      numeric: true,
      render: (r) => (
        <span className={r.pending_count ? "font-medium text-warning" : "text-muted-foreground"}>
          {num(r.pending_count)}
        </span>
      ),
    },
    {
      key: "notes",
      header: "Note",
      render: (r) =>
        r.notes ? (
          <span className="text-xs text-muted-foreground">{r.notes}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
  ];
}

function HowToTab() {
  return (
    <Card>
      <CardContent className="prose prose-sm dark:prose-invert max-w-none py-6">
        <h3>What this step is for</h3>
        <p>
          Some Cinch accounts are not customers. <strong>Cinch - Product</strong>,{" "}
          <strong>Cinch - R&amp;D</strong>, <strong>Cinch - Marketing</strong> and
          the rest exist so we can demo, train, test and run our own campaigns.
          Their usage is real spend that belongs on a department&rsquo;s budget,
          not on a customer&rsquo;s invoice.
        </p>
        <p>Two things go wrong, in opposite directions, and both cost money:</p>
        <ul>
          <li>
            A <strong>customer</strong> company mis-parented to an internal
            account is revenue we never billed. July 2026 found{" "}
            <code>South Bay Lube</code> sitting on Cinch - Sales with 34 locations
            and 288,950 contacts, while the real, currently-billing{" "}
            <code>South Bay Lube, Inc. DBA Jiffy Lube</code> lived on its own
            account.
          </li>
          <li>
            An <strong>internal</strong> company left on a customer account bills
            someone else for our own usage. That is what the Recommended tab
            hunts for.
          </li>
        </ul>

        <h3>How to work it</h3>
        <ol>
          <li>
            <strong>Pull the month.</strong> Every company under a roster account,
            with the contacts it held and the texts it sent.
          </li>
          <li>
            <strong>Filter, then verify in bulk.</strong> Filter to one account —
            or to &ldquo;no texts&rdquo; — and the bulk bar applies your verdict to
            exactly the rows on screen. It names the count in the button and warns
            you when rows are filtered out, so the scope is never a guess.
          </li>
          <li>
            <strong>Flag what you can&rsquo;t accept.</strong> An open flag blocks
            completing R-17, because completing it <em>is</em> the attestation that
            every company here belongs here.
          </li>
          <li>
            <strong>Work the Recommended tab.</strong> Confirm the ones that really
            are ours, and dismiss the ones that are not — a dismissal is durable,
            so a customer&rsquo;s own sandbox never comes back next month.
          </li>
        </ol>

        <h3>Reading the columns</h3>
        <ul>
          <li>
            <strong>Segments vs messages.</strong> Segments are what carriers bill;
            a long message costs several. Cinch - Marketing sent 1,618 messages in
            July 2026 and paid for 4,543 segments.
          </li>
          <li>
            <strong>Contacts</strong> is much the biggest number here — 1.9M across
            the roster. It costs nothing today, and would cost a great deal the day
            contacts are charged back, which is why it is verified now rather than
            when it becomes expensive.
          </li>
          <li>
            <strong>Billed</strong> is deliberately empty. Internal usage
            isn&rsquo;t priced automatically yet; the column ships blank so the
            report&rsquo;s shape doesn&rsquo;t change under anyone the month it
            starts carrying numbers.
          </li>
          <li>
            <strong>Reviewed by</strong> names who accepted the charge, next to the
            number they accepted. This step feeds accounting, so &ldquo;who signed
            for this&rdquo; belongs on the line, not in a tooltip.
          </li>
        </ul>

        <h3>Why the roster is a stored list</h3>
        <p>
          It would be tempting to select{" "}
          <code>ACCOUNTS.ACCOUNT_TYPE = 'Internal'</code>. That tag covers only six
          of the ten accounts that actually are — <code>Cinch</code>,{" "}
          <code>Cinch - R&amp;D</code>, <code>Justin made companies</code> and{" "}
          <code>Project Upskill Companies - Cinch</code> all carry no tag at all.
          Deriving the set would silently drop four accounts today and keep
          dropping every new one nobody remembers to tag. A stored roster fails the
          other way: a genuinely new internal account shows up on the Recommended
          tab until someone adds it, which is a nuisance rather than a hole.
        </p>

        <h3>What the Recommended tab does NOT use</h3>
        <p>
          &ldquo;The account billed $0 this month&rdquo; sounds like a perfect
          signal and is not one. <code>Moxie Pest Control</code> bills $0 under its
          own QuickBooks id because the revenue posts to the merged counterparty{" "}
          <code>Moxie Advisors</code> — so every merged account would look internal.
          Account folding makes revenue useless as an internal-vs-customer test,
          which is why the signals are the company&rsquo;s name and whether every
          user on it is <code>@cinch.io</code>. The second one is noisy on its own
          (a customer whose only user is their CSM looks identical), so the table
          always names which check fired.
        </p>
      </CardContent>
    </Card>
  );
}
